import React, { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Native-feel Pull-to-Refresh wrapper.
 * - Only triggers when the scroll container (window) is at top
 * - User pulls down ≥ THRESHOLD → release → onRefresh() runs
 * - Default onRefresh = window.location.reload() → full page refresh
 *
 * IMPORTANT: We do NOT wrap children with any CSS `transform` wrapper,
 * because an ancestor transform pins descendant `position: fixed`
 * to the transformed box (breaks modals/overlays) AND forces huge
 * repaints on every pointer move (visible as a hang on low-end
 * phones). Only the indicator moves; the page stays rock-solid.
 */
const THRESHOLD = 70;    // pixels user must pull before refresh triggers
const MAX_PULL  = 110;   // visual max

export default function PullToRefresh({ onRefresh, children }) {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const activePull = useRef(false);

  const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

  const handleStart = useCallback((e) => {
    if (!atTop() || refreshing) return;
    startY.current = e.touches ? e.touches[0].clientY : e.clientY;
    activePull.current = false;
  }, [refreshing]);

  const handleMove = useCallback((e) => {
    if (startY.current == null || refreshing) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = y - startY.current;
    if (delta <= 0) {
      // user is swiping up — abort pull
      if (activePull.current) { activePull.current = false; setPullY(0); }
      return;
    }
    if (!atTop()) { startY.current = null; return; }
    activePull.current = true;
    // Resistive feel — damp the pull so it slows down near MAX_PULL
    const damped = Math.min(MAX_PULL, delta * 0.5);
    setPullY(damped);
    if (e.cancelable && delta > 8) e.preventDefault();
  }, [refreshing]);

  const handleEnd = useCallback(async () => {
    if (!activePull.current) {
      startY.current = null;
      return;
    }
    activePull.current = false;
    startY.current = null;
    if (pullY >= THRESHOLD) {
      setRefreshing(true);
      setPullY(THRESHOLD);
      try {
        if (onRefresh) {
          await onRefresh();
          setRefreshing(false);
          setPullY(0);
        } else {
          // small delay so the spinner is visible before reload kicks in
          await new Promise((r) => setTimeout(r, 300));
          window.location.reload();
        }
      } catch (_e) {
        setRefreshing(false);
        setPullY(0);
      }
    } else {
      setPullY(0);
    }
  }, [pullY, onRefresh]);

  useEffect(() => {
    const opts = { passive: false };
    window.addEventListener("touchstart", handleStart, opts);
    window.addEventListener("touchmove",  handleMove,  opts);
    window.addEventListener("touchend",   handleEnd);
    window.addEventListener("touchcancel", handleEnd);
    return () => {
      window.removeEventListener("touchstart", handleStart);
      window.removeEventListener("touchmove",  handleMove);
      window.removeEventListener("touchend",   handleEnd);
      window.removeEventListener("touchcancel", handleEnd);
    };
  }, [handleStart, handleMove, handleEnd]);

  const progress = Math.min(1, pullY / THRESHOLD);
  const show = pullY > 4 || refreshing;

  return (
    <>
      {/* Indicator only — content stays put, so no layout thrash, no hang */}
      <div
        aria-hidden
        className="fixed top-0 left-1/2 z-[9999] pointer-events-none select-none"
        style={{
          transform: `translate3d(-50%, ${show ? Math.max(6, pullY - 20) : -64}px, 0)`,
          transition: refreshing || pullY === 0 ? "transform 240ms cubic-bezier(.3,.9,.3,1), opacity 180ms" : "none",
          opacity: show ? 1 : 0,
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className="w-11 h-11 rounded-full bg-slate-900 border-2 border-cyan-400/50 shadow-xl flex items-center justify-center"
          style={{
            borderColor: progress >= 1 || refreshing ? "#f59e0b" : "#cbd5e1",
            boxShadow: "0 10px 24px rgba(0,0,0,.18)",
          }}
        >
          <RefreshCw
            className={refreshing ? "w-5 h-5 animate-spin text-amber-600" : "w-5 h-5 text-amber-600"}
            style={{ transform: refreshing ? undefined : `rotate(${progress * 300}deg)` }}
            strokeWidth={2.6}
          />
        </div>
      </div>
      {children}
    </>
  );
}
